import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { SquareEqual, X, Edit, Trash2, Plus } from "lucide-react";

const CategoryData = [
  { id: 1, name: "LIME PLASTER", rate: 150 },
  { id: 2, name: "CONCRETE CONCEPT", rate: 150 },
  { id: 3, name: "MATT STUCCO", rate: 150 },
  { id: 4, name: "FENICE LUXURY", rate: 180 },
  { id: 5, name: "OTTOCENTO(ORO)", rate: 180 },
  { id: 6, name: "ENCANTO(FLOE)", rate: 180 },
  { id: 7, name: "OPECO MATT", rate: 180 },
  { id: 8, name: "STENCIL ART", rate: 180 },
  { id: 9, name: "CEMENTO", rate: 350 },
  { id: 10, name: "STONE EFFECT (Classic Effect)", rate: 450 },
  { id: 11, name: "STAMPED CONCRETE WALL", rate: 350 },
  { id: 12, name: "GRANITE", rate: 110 },
  { id: 13, name: "FLAKES", rate: 110 },
  { id: 14, name: "FINE FLAKES", rate: 110 },
  { id: 15, name: "NATURAL GRIT FINISH", rate: 110 },
  { id: 17, name: "SMOOTH STONE", rate: 130 },
  { id: 18, name: "STONE GRACE", rate: 130 },
  { id: 19, name: "NATURAL STONE", rate: 250 },
  { id: 20, name: "MUD FINISH", rate: 250 },
  { id: 21, name: "LIQUID METAL(METAL LINE)", rate: 450 },
];

const calculateDiscount = (sqft) => {
  const ranges = [
    { min: 1, max: 100, discount: 0 },
    { min: 100, max: 500, startDiscount: 0, endDiscount: 6.6 },
    { min: 500, max: 1000, startDiscount: 6.6, endDiscount: 13.3 },
    { min: 1000, max: 2000, startDiscount: 13.3, endDiscount: 20 },
    { min: 2000, max: 3000, startDiscount: 20, endDiscount: 23.3 },
    { min: 3000, max: 5000, startDiscount: 23.3, endDiscount: 26.6 },
    { min: 5000, max: 6000, startDiscount: 26.6, endDiscount: 33.3 },
    { min: 6000, max: 7000, startDiscount: 33.3, endDiscount: 36.6 },
    { min: 7000, max: 10000, startDiscount: 36.6, endDiscount: 40 },
    { min: 10000, max: Infinity, discount: 40 },
  ];

  const range = ranges.find((range) => sqft >= range.min && sqft <= range.max);

  if (range?.startDiscount !== undefined) {
    // Calculate the discount using linear interpolation
    const ratio = (sqft - range.min) / (range.max - range.min);
    return (
      range.startDiscount + ratio * (range.endDiscount - range.startDiscount)
    );
  } else {
    return range?.discount;
  }
};

const ConcreteMattPage = () => {
  const { id, page, main_cat_id, CategoryId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    pincode: "",
    address: "",
    sqft: "",
  });
  // Quotation form state
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    address: "",
    pincode: "",
    selectedLocation: "",
    professionalProducts: "",
    productsPattern: "",
    productName: "",
    rate: "",
    sqft: "",
    amount: "",
    discount: 0,
    effectiveRate: 0,
    hsnSac: "995473", // Add HSN/SAC code
    igst: 18, // Add IGST percentage
  });

  useEffect(() => {
    if (formData?.sqft) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        sqft: formData.sqft ? "" : "Sq Ft is required",
      }));
    }
    if (formData?.mobile) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        mobile:
          formData.mobile.length === 10
            ? ""
            : "Mobile Number must be 10 digits",
      }));
    }
    if (formData?.pincode) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        pincode:
          formData.pincode.length === 6 ? "" : "Pincode must be 6 digits",
      }));
    }

    if (formData?.name) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: formData.name ? "" : "Name is required",
      }));
    }
    if (formData?.address) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        address: formData.address ? "" : "Address is required",
      }));
    }
    if (formData?.selectedLocation) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        selectedLocation: formData.selectedLocation
          ? ""
          : "Location is required",
      }));
    }
  }, [
    formData.name,
    formData.mobile,
    formData.pincode,
    formData.address,
    formData.sqft,
    formData.selectedLocation,
  ]);

  const [pincodeData, setPincodeData] = useState([]);
  const [quotationItems, setQuotationItems] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `https://naturalx.in/admin/api/getproduct?main_cat_id=${main_cat_id}&cat_id=${CategoryId}&page=${page}`
      );
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data.data?.data);
      setTotalPages(data.data?.last_page);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, id, main_cat_id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const memoizedProducts = useMemo(() => products, [products]);
  const selectedProduct = memoizedProducts?.find(
    (product) => product.id === parseInt(id)
  );

  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    setMainImage(selectedProduct?.image);
  }, [selectedProduct]);

  const thumbnails = [
    selectedProduct?.image,
    ...(selectedProduct?.gallery?.map((imgObj) => imgObj.img) || []),
  ].filter(Boolean);

  const handleThumbnailClick = (image) => {
    setMainImage(image);
  };

  const otherProducts = memoizedProducts
    .filter((product) => product.id !== parseInt(id))
    .slice(0, 3);

  const businessPhoneNumber = "+91 99988 52256";
  const whatsappMessage = `Hello! I am interested in *${selectedProduct?.title}*.
Category: ${selectedProduct?.category}
Description: ${selectedProduct?.description}

Can you provide more details?`;

  const displayedThumbnails = showAll ? thumbnails : thumbnails.slice(0, 4);

  const handleWhatsAppClick = () => {
    const whatsappUrl = `whatsapp://send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
      whatsappMessage
    )}`;
    window.location.href = whatsappUrl;
  };

  // Handle pincode API call
  const fetchPincodeData = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`
        );
        const data = await response.json();
        if (data[0].Status === "Success") {
          setPincodeData(data[0].PostOffice);
        } else {
          setPincodeData([]);
        }
      } catch (error) {
        console.error("Error fetching pincode data:", error);
        setPincodeData([]);
      }
    } else {
      setPincodeData([]);
    }
  };

  // Auto-populate rate based on category
  useEffect(() => {
    const selectedCategory = CategoryData.find(
      (category) => category.id === parseInt(CategoryId)
    );
    if (selectedCategory) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        rate: selectedCategory.rate.toString(),
      }));
    }
  }, [CategoryId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validate mobile number (only digits, max 10)
    if (name === "mobile") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
      return;
    }

    // Validate pincode (only digits, max 6)
    if (name === "pincode") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 6) {
        setFormData({ ...formData, [name]: numericValue });
        fetchPincodeData(numericValue);
      }
      return;
    }

    // Validate sqft (only numeric)
    if (name === "sqft") {
      const numericValue = value.replace(/[^0-9.]/g, "");
      setFormData({ ...formData, [name]: numericValue });

      // Calculate discount
      const sqftValue = parseFloat(numericValue) || 0;
      const discount = calculateDiscount(sqftValue);

      // Calculate effective rate
      const rate = parseFloat(formData.rate) || 0;
      const effectiveRate = rate * (1 - discount / 100);

      // Calculate amount
      const amount = effectiveRate * sqftValue;
      const igst = parseFloat(formData.igst) || 0;
      const amountWithIgst = amount * (1 + igst / 100);

      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
        discount: discount?.toFixed(2),
        effectiveRate: effectiveRate?.toFixed(2),
        amount: amount?.toFixed(2),
        amountWithIgst: amountWithIgst?.toFixed(2),
      }));
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  // Add item to quotation
  const handleAddItem = () => {
    const newItem = {
      professionalProducts: formData.professionalProducts,
      productsPattern: formData.productsPattern,
      productName: formData.productName,
      rate: parseFloat(formData.rate),
      sqft: parseFloat(formData.sqft),
      discount: parseFloat(formData.discount),
      effectiveRate: parseFloat(formData.effectiveRate),
      amount: parseFloat(formData.amount),
    };

    if (editingIndex >= 0) {
      const updated = [...quotationItems];
      updated[editingIndex] = newItem;
      setQuotationItems(updated);
      setEditingIndex(-1);
    } else {
      setQuotationItems([...quotationItems, newItem]);
    }

    // Reset product fields
    setFormData({
      ...formData,
      professionalProducts: "",
      productsPattern: "",
      productName: "",
      rate: "",
      sqft: "",
      discount: 0,
      effectiveRate: 0,
      amount: "",
    });
  };

  // Edit item
  const handleEditItem = (index) => {
    const item = quotationItems[index];
    setFormData({
      ...formData,
      professionalProducts: item.professionalProducts,
      productsPattern: item.productsPattern,
      productName: item.productName,
      rate: item.rate.toString(),
      sqft: item.sqft.toString(),
      discount: item.discount.toString(),
      effectiveRate: item.effectiveRate.toString(),
      amount: item.amount.toString(),
    });
    setEditingIndex(index);
  };

  // Delete item
  const handleDeleteItem = (index) => {
    const updated = quotationItems.filter((_, i) => i !== index);
    setQuotationItems(updated);
  };

  // Calculate total amount
  const totalAmount = quotationItems.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  const [isLoadingWhatsAppQuote, setIsLoadingWhatsAppQuote] = useState(false);

  // Handle print
  // Handle WhatsApp quote button click
  const handleWhatsAppQuote = async () => {
    setErrors({
      name: "",
      mobile: "",
      pincode: "",
      address: "",
      sqft: "",
    });
    let isValid = true;
    const newErrors = { ...errors };

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile Number is required";
      isValid = false;
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = "Mobile Number must be 10 digits";
      isValid = false;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
      isValid = false;
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = "Pincode must be 6 digits";
      isValid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }

    if (!formData.sqft.trim()) {
      newErrors.sqft = "Sq Ft is required";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      return;
    }

    const message = `Hello! I am interested in a quote for the following details:
  Name: ${formData.name}
  Mobile: ${formData.mobile}
  Address: ${formData.address}
  Pincode: ${formData.pincode}
  Location: ${formData.selectedLocation}
  Category: ${selectedCategoryName || "N/A"}
  Product: ${selectedProduct?.title || formData.productName}
  Rate: ${formData.rate}
  Discount: ${formData.discount + "%"}
  Discount Rate: ${formData?.effectiveRate}
  Sq Ft: ${formData.sqft}
  Amount: ${formData.amount}
  HSN/SAC: ${formData.hsnSac}
  IGST: ${formData.igst + "%"}
  Amount with IGST: ${formData.amountWithIgst}`;

    setIsLoadingWhatsAppQuote(true);
    try {
      // Call the API to save contact
      const response = await fetch(
        `https://naturalx.in/admin/api/save_contact?name=${encodeURIComponent(
          formData.name
        )}&phone=${encodeURIComponent(
          formData.mobile
        )}&email=${encodeURIComponent(
          formData.email || "-"
        )}&msg=${encodeURIComponent(message)}`
      );

      if (!response.ok) {
        throw new Error("Failed to save contact");
      }

      // Redirect to WhatsApp after a delay
      setTimeout(() => {
        window.location.href = `whatsapp://send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
          `New form submission: ${message}`
        )}`;
        setIsLoadingWhatsAppQuote(false);
        setShowQuotationModal(false);
      }, 1500);
    } catch (error) {
      console.error("Error saving contact:", error);
      setIsLoadingWhatsAppQuote(false);
      setShowQuotationModal(false);
      alert("Failed to send WhatsApp message. Please try again later.");
    }
  };

  // Update the button in the Quotation Modal
  <button
    onClick={handleWhatsAppQuote}
    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-300"
  >
    Quote on WhatsApp
  </button>;

  // Find the selected category name based on CategoryId
  const selectedCategoryName = CategoryData.find(
    (category) => category.id === parseInt(CategoryId)
  )?.name;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mt-12">
      <Helmet>
        <title>{`${selectedProduct?.title} - Professional Products Pattern`}</title>
        <meta
          name="description"
          content={`Discover ${selectedProduct?.title}, a professional product pattern in the category of ${selectedProduct?.category}. Learn more about its features and benefits.`}
        />
        <meta
          name="keywords"
          content={`${selectedProduct?.title}, professional products, ${selectedProduct?.category}, product details`}
        />
        <meta
          property="og:title"
          content={`${selectedProduct?.title} - Professional Products Pattern`}
        />
        <meta
          property="og:description"
          content={`Discover ${selectedProduct?.title}, a professional product pattern in the category of ${selectedProduct?.category}. Learn more about its features and benefits.`}
        />
        <meta property="og:image" content={selectedProduct?.image} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <h2 className="text-xl mb-4 sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight ">
        Professional Products Pattern:
        <span className="border-b-4 border-orange-500 ml-2">
          {selectedProduct?.title}
        </span>
      </h2>

      <div className="grid grid-cols-1 mt-8 md:grid-cols-2 gap-8">
        {/* Left Side - Image Gallery */}
        <div className="flex flex-col space-y-4">
          <div className="bg-white rounded-lg overflow-hidden shadow-md">
            <a href={mainImage} target="_blank" rel="noopener noreferrer">
              <img
                src={mainImage}
                alt={`${selectedProduct?.title} - Concrete Matt finish`}
                className="w-full h-[300px] sm:h-[400px] md:h-[500px] object-cover cursor-pointer"
              />
            </a>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {displayedThumbnails.map((thumb, index) => (
              <div
                key={index}
                className="relative bg-white rounded-md overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition duration-300"
                onClick={() => handleThumbnailClick(thumb)}
              >
                <img
                  src={thumb}
                  alt={`Thumbnail ${index + 1} of ${selectedProduct?.title}`}
                  className="w-full h-20 sm:h-24 object-cover"
                />
                {!showAll && index === 3 && thumbnails.length > 4 && (
                  <div
                    className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-sm sm:text-base font-semibold"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAll(true);
                    }}
                  >
                    +{thumbnails.length - 4} more
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Product Info */}
        <div className="flex flex-col space-y-6">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">
            {selectedProduct?.title}
          </h1>

          <div className="prose prose-lg text-gray-600">
            <p>{selectedProduct?.description}</p>
          </div>

          <p className="text-sm text-gray-600">
            Product:{" "}
            <span className="font-medium">{selectedProduct?.category}</span>
          </p>

          <button
            onClick={handleWhatsAppClick}
            className="w-full flex items-center justify-center bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-500 transition duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-6 h-6 mr-2"
            >
              <path
                fillRule="evenodd"
                d="M12 0C5.373 0 0 4.99 0 11.143c0 2.144.606 4.233 1.756 6.034L.098 23.375l6.449-1.663a11.877 11.877 0 005.453 1.363h.002c6.627 0 12-4.99 12-11.143C24 4.99 18.627 0 12 0zm0 20.571a9.363 9.363 0 01-4.863-1.325l-.347-.21-3.829 1.01 1.02-3.605-.226-.37a9.161 9.161 0 01-1.397-4.427c0-4.998 4.477-9.071 10-9.071s10 4.073 10 9.071-4.477 9.072-10 9.072zm5.45-7.196c-.298-.149-1.763-.87-2.036-.97-.273-.1-.472-.149-.671.149s-.77.97-.947 1.168c-.173.199-.347.224-.645.075-.298-.149-1.259-.464-2.4-1.479-.886-.79-1.484-1.767-1.659-2.065s-.018-.461.131-.61c.135-.135.298-.347.447-.521.151-.174.2-.299.3-.498.1-.198.05-.373-.025-.521-.075-.149-.671-1.619-.92-2.22-.243-.583-.488-.505-.671-.515-.173-.008-.372-.009-.571-.009s-.523.075-.797.373c-.274.298-1.048 1.022-1.048 2.494s1.074 2.895 1.223 3.097c.15.199 2.11 3.223 5.133 4.521.718.31 1.278.495 1.717.634.722.229 1.377.197 1.894.12.577-.088 1.763-.72 2.01-1.415.248-.695.248-1.29.173-1.415-.074-.124-.273-.198-.571-.347z"
                clipRule="evenodd"
              />
            </svg>
            Request a Quote on WhatsApp
          </button>
          <button
            onClick={() => setShowQuotationModal(true)}
            className="w-full flex items-center justify-center bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-500 transition duration-300 mt-2"
          >
            <SquareEqual className="mr-2" />
            Get Quotation
          </button>
        </div>
      </div>

      {/* Other Products Section */}
      <div className="mt-20 text-start text-xl font-semibold text-gray-900 border-b-2 pb-4">
        Related Other Products Pattern
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
        {otherProducts.map((product) => (
          <div
            key={product.id}
            className="bg-white p-4 sm:p-6 rounded-lg shadow-md"
          >
            <h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-3">
              {product.title}
            </h3>
            <img
              src={product.image}
              alt={product.title}
              loading="lazy"
              className="w-full h-[180px] sm:h-[200px] object-cover rounded-lg hover:scale-105 transition-transform duration-300"
            />
            <p className="text-gray-600 text-sm sm:text-base mt-2 sm:mt-3">
              {product.description}
            </p>
            <div className="mt-3">
              <a
                href={`/product/${product.id}/${page}/${main_cat_id}/${CategoryId}/${product.title}`}
                className="block text-blue-600 hover:underline"
              >
                View Product →
              </a>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 text-center">
        <a
          href={`/collections/${CategoryId}/${main_cat_id}`}
          className="inline-block bg-orange-500 text-white px-5 py-2 rounded-md hover:bg-orange-600 transition duration-300"
        >
          See All Patterns
        </a>
      </div>

      {/* Quotation Modal */}
      {showQuotationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Quotation Form
              </h2>
              <button
                onClick={() => setShowQuotationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {/* Bill To Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  Bill To
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter Mobile number"
                      maxLength="10"
                    />
                    {errors.mobile && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.mobile}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your address"
                      rows="3"
                    />
                    {errors.address && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.address}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pincode
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter 6 digit pincode"
                      maxLength="6"
                    />
                    {errors.pincode && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.pincode}
                      </p>
                    )}
                    {formData.pincode.length === 6 &&
                      pincodeData.length > 0 && (
                        <div className="mt-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Location
                          </label>
                          <select
                            name="selectedLocation"
                            value={formData.selectedLocation}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select Location</option>
                            {pincodeData.map((location, index) => (
                              <option key={index} value={location.Name}>
                                {location.Name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Products Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  Products
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={selectedCategoryName || ""}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name
                    </label>
                    <input
                      type="text"
                      name="productName"
                      value={selectedProduct?.title || formData.productName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      placeholder="Auto populated"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate
                    </label>
                    <input
                      type="text"
                      name="rate"
                      value={formData.rate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      placeholder="Auto populated"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sq Ft
                    </label>
                    <input
                      type="text"
                      name="sqft"
                      required
                      value={formData.sqft}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter square feet"
                    />
                    {errors.sqft && (
                      <p className="mt-1 text-sm text-red-600">{errors.sqft}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount (%)
                    </label>
                    <input
                      type="text"
                      name="discount"
                      value={formData.discount ? formData.discount : 0 + "%"}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      placeholder="Auto calculated"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      discount Rate
                    </label>
                    <input
                      type="text"
                      name="effectiveRate"
                      value={
                        formData?.effectiveRate === "NaN"
                          ? ""
                          : formData?.effectiveRate || ""
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      placeholder="Auto calculated"
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST (%)
                    </label>
                    <input
                      type="text"
                      name="igst"
                      value={formData.igst + "%"}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount
                    </label>
                    <input
                      type="text"
                      name="amount"
                      value={
                        formData?.amount === "NaN" ? "" : formData?.amount || ""
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      placeholder="Auto calculated"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount with IGST
                    </label>
                    <input
                      type="text"
                      name="amountWithIgst"
                      value={
                        formData?.amountWithIgst === "NaN"
                          ? ""
                          : formData?.amountWithIgst || ""
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                      placeholder="Auto calculated"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowQuotationModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-300"
                >
                  Close
                </button>
                <button
                  onClick={handleWhatsAppQuote}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-300 flex items-center justify-center"
                  disabled={isLoadingWhatsAppQuote}
                >
                  {isLoadingWhatsAppQuote ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Quote on WhatsApp"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConcreteMattPage;
